<template>
  <div v-if="show" class="modal-overlay saved-experiences-modal" @click="emitClose">
    <div class="modal-container saved-experiences-modal__container" @click.stop>
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          aria-label="Close saved experiences"
          @click="emitClose"
        >
          ×
        </button>
        <h3>Saved {{ experienceType }} experiences</h3>
        <p class="saved-experiences-modal__description">
          Pick a previously published experience to attach, or open the full library.
        </p>
      </div>

      <div class="modal-body saved-experiences-modal__body">
        <div v-if="publishedOptions.length === 0" class="experience-attach-empty">
          No published {{ experienceType }} experiences yet. Publish one from the experience library first.
        </div>

        <div v-else class="experience-attach-select">
          <div class="experience-attach-select__head">
            <span>Saved {{ experienceType }} experiences</span>
            <span
              v-if="publishedOptions.length > initialVisibleCount"
              class="experience-attach-select__hint"
            >
              {{ visibleRangeLabel }}
            </span>
          </div>
          <div class="experience-attach-list">
            <div class="experience-attach-group">
              <span class="experience-attach-group__title">Published</span>
              <button
                v-for="record in visibleOptions"
                :key="record.localKey"
                type="button"
                class="experience-attach-record"
                :class="{ 'experience-attach-record--active': selectedDraftKey === record.localKey }"
                @click="onSelect(record.localKey)"
              >
                <div class="experience-attach-record__top">
                  <div class="experience-attach-record__identity">
                    <span class="experience-attach-record__name">{{ record.name }}</span>
                    <span class="experience-attach-record__key">{{ record.localKey }}</span>
                  </div>
                  <div class="experience-attach-record__badges">
                    <span
                      class="experience-attach-record__status"
                      :class="`experience-attach-record__status--${record.status}`"
                    >
                      {{ record.status }}
                    </span>
                    <span class="experience-attach-record__version">v{{ record.versionNumber || 1 }}</span>
                  </div>
                </div>
                <div class="experience-attach-record__meta">
                  <span>{{ record.templateFamilyId || 'General template' }}</span>
                  <span>•</span>
                  <span>{{ record.rounds }} rounds</span>
                  <span>•</span>
                  <span>{{ record.stages }} stages</span>
                  <span>•</span>
                  <span>{{ record.questions }} questions</span>
                </div>
              </button>
            </div>
          </div>
          <div
            v-if="publishedOptions.length > initialVisibleCount"
            class="experience-attach-pagination"
          >
            <button
              type="button"
              class="experience-attach-pagination__button"
              :disabled="!canPageBackward"
              @click="page(-1)"
            >
              Previous
            </button>
            <button
              type="button"
              class="experience-attach-pagination__button"
              :disabled="!canPageForward"
              @click="page(1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div class="modal-footer saved-experiences-modal__footer">
        <router-link
          class="saved-experiences-modal__action saved-experiences-modal__action--secondary"
          :to="{ path: '/dashboard/experiences' }"
          @click="emitClose"
        >
          Open Library
        </router-link>
        <button
          type="button"
          class="saved-experiences-modal__action saved-experiences-modal__action--primary"
          @click="emitClose"
        >
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from 'vue'
import type { ExperienceLibraryRecord } from '@/utils/experienceLibrary'

export default defineComponent({
  name: 'SavedExperiencesModal',
  props: {
    show: { type: Boolean, default: false },
    experienceType: { type: String as PropType<'quiz' | 'survey'>, required: true },
    publishedOptions: {
      type: Array as PropType<ExperienceLibraryRecord[]>,
      default: () => [],
    },
    selectedDraftKey: { type: String, default: '' },
    initialVisibleCount: { type: Number, default: 3 },
  },
  emits: ['close', 'select'],
  setup(props, { emit }) {
    const pageIndex = ref(0)

    const pageCount = computed(() =>
      Math.max(Math.ceil(props.publishedOptions.length / props.initialVisibleCount), 1),
    )

    const visibleOptions = computed(() =>
      props.publishedOptions.slice(
        pageIndex.value * props.initialVisibleCount,
        (pageIndex.value + 1) * props.initialVisibleCount,
      ),
    )

    const visibleRangeLabel = computed(() => {
      if (props.publishedOptions.length === 0) return '0 of 0'
      const start = pageIndex.value * props.initialVisibleCount + 1
      const end = Math.min(
        (pageIndex.value + 1) * props.initialVisibleCount,
        props.publishedOptions.length,
      )
      return `${start}-${end} of ${props.publishedOptions.length}`
    })

    const canPageBackward = computed(() => pageIndex.value > 0)
    const canPageForward = computed(() => pageIndex.value < pageCount.value - 1)

    watch(
      () => props.publishedOptions.length,
      (length) => {
        const maxPageIndex = Math.max(Math.ceil(length / props.initialVisibleCount) - 1, 0)
        if (pageIndex.value > maxPageIndex) {
          pageIndex.value = maxPageIndex
        }
      },
      { immediate: true },
    )

    watch(
      () => props.show,
      (next) => {
        if (next) {
          pageIndex.value = 0
        }
      },
    )

    const page = (direction: -1 | 1) => {
      const next = pageIndex.value + direction
      if (next < 0 || next >= pageCount.value) return
      pageIndex.value = next
    }

    const onSelect = (localKey: string) => {
      emit('select', localKey)
    }

    const emitClose = () => emit('close')

    return {
      pageIndex,
      visibleOptions,
      visibleRangeLabel,
      canPageBackward,
      canPageForward,
      page,
      onSelect,
      emitClose,
    }
  },
})
</script>

<style scoped>
.saved-experiences-modal__container {
  max-width: 720px;
  width: 100%;
}

.saved-experiences-modal__description {
  margin: 0.25rem 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.saved-experiences-modal__body {
  padding: 1rem 1.25rem;
}

.saved-experiences-modal__footer {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem 1.1rem;
}

/* Twin footer actions — equal-width, edge-to-edge. Primary fills accent,
   secondary stays outlined. Both override the global button{} reset. */
.saved-experiences-modal__action {
  /* Override the global button{} reset (width:100%, height:3.125rem, etc). */
  width: auto;
  height: 3.25rem;
  margin: 0;
  padding: 0 1.25rem;
  box-sizing: border-box;
  box-shadow: none;
  overflow: visible;

  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1.5px solid var(--vt-c-accent);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
}

.saved-experiences-modal__action--primary {
  background: var(--vt-c-accent);
  color: #0a1628;
}

.saved-experiences-modal__action--primary:hover,
.saved-experiences-modal__action--primary:focus-visible {
  background: rgba(57, 255, 20, 0.8);
  outline: none;
}

.saved-experiences-modal__action--secondary {
  background: transparent;
  color: var(--vt-c-accent);
}

.saved-experiences-modal__action--secondary:hover,
.saved-experiences-modal__action--secondary:focus-visible {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  outline: none;
}

/* These element selectors are wrapped under the modal's BEM root, but the
   global button{} reset in src/assets/base.css still wins on width/height/
   margin/background. Re-state explicitly so records render as proper cards
   rather than gradient-filled pills. */
.experience-attach-select__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0 0.25rem 0.5rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.65);
}

.experience-attach-select__hint {
  font-weight: 600;
  color: rgba(var(--vt-c-accent-rgb), 0.85);
}

.experience-attach-list {
  display: grid;
  gap: 0.6rem;
}

.experience-attach-group {
  display: grid;
  gap: 0.5rem;
}

.experience-attach-group__title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(var(--vt-c-accent-rgb), 0.85);
}

.experience-attach-empty {
  padding: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 0.6rem;
}

.experience-attach-record {
  /* Override global button{} reset. */
  width: 100%;
  height: auto;
  min-height: 0;
  margin: 0;
  background: rgba(255, 255, 255, 0.04);
  color: var(--color-text);
  box-shadow: none;
  font-size: 0.92rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  overflow: visible;

  display: grid;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
}

.experience-attach-record:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(var(--vt-c-accent-rgb), 0.4);
}

.experience-attach-record--active {
  border-color: var(--vt-c-accent);
  background: rgba(var(--vt-c-accent-rgb), 0.1);
  box-shadow: 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.3);
}

.experience-attach-record__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.experience-attach-record__identity {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.experience-attach-record__name {
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.25;
}

.experience-attach-record__key {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.74rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-word;
}

.experience-attach-record__badges {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}

.experience-attach-record__status {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.experience-attach-record__status--draft {
  background: rgba(255, 209, 102, 0.14);
  color: #ffd166;
}

.experience-attach-record__status--published {
  background: rgba(57, 255, 20, 0.14);
  color: #8dff78;
}

.experience-attach-record__version {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.78);
  background: rgba(255, 255, 255, 0.08);
}

.experience-attach-record__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
}

.experience-attach-pagination {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.6rem;
}

.experience-attach-pagination__button {
  /* Override global button{} reset. */
  width: auto;
  height: auto;
  margin: 0;
  background: rgba(255, 255, 255, 0.04);
  color: var(--vt-c-accent);
  box-shadow: none;
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: normal;
  overflow: visible;

  flex: 1 1 auto;
  min-width: 0;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.3);
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
}

.experience-attach-pagination__button:hover:not(:disabled) {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  border-color: var(--vt-c-accent);
}

.experience-attach-pagination__button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

</style>
